import { DownOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Modal, Row, Typography } from 'antd';
import { useSession } from 'next-auth/react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomizedDetailedUser } from '../../hooks/useUserById';
import { deleteUser } from '../../services/people/userServices';
import { useTabStore } from '../../store/RootStoreContext';
import { grispiNotification } from '../../store/useNotificationStore';
import { isAdmin, isEndUser } from '../../utils/accountHelper';
import requestWithMessage from '../../utils/requestWithMessage';
import { redirectTo } from '../../utils/routeHelper';
import UserMergeModal, { UserMergeModalRef } from './UserMergeModal';
const { Text } = Typography;

/* A way to define a set of constants. */
enum UserActionMap {
    delete = 'delete',
    merge = 'merge'
}

export default function UserActionButton({ user }: { user: CustomizedDetailedUser }) {
    const { t } = useTranslation('common');
    const { t: tPeople } = useTranslation('people');
    const {
        data: {
            userInfo: { rls }
        }
    } = useSession();

    const { closeTabAndGetNewTargetUrl } = useTabStore();
    const modalRef = useRef<UserMergeModalRef>(null);
    const isMergableUser = isEndUser(user?.role?.authority);
    const currentUserRole = rls[0];
    const currentUserCanDeleteCustomer = isAdmin(currentUserRole);

    const [modal, contextHolder] = Modal.useModal();
    const handleMenuClick: MenuProps['onClick'] = async (info) => {
        try {
            if (info.key === UserActionMap.merge) {
                modalRef.current.open();
                return;
            }

            if (info.key === UserActionMap.delete) {
                showPromiseConfirm();
            }
        } catch (error) {
            return;
        }
    };
    const showPromiseConfirm = () => {
        modal.confirm({
            title: <Text strong>{tPeople('popConfirmMessage.onDelete.title')}</Text>,
            icon: <ExclamationCircleOutlined />,
            content: <Text strong>{tPeople('popConfirmMessage.onDelete.info2')}</Text>,
            onOk() {
                return deleteUserAction();
            },
            okText: t('delete')
        });
    };
    const deleteUserAction = async () => {
        const startMessage = tPeople('startMessage.onDelete');
        const finishMessage = tPeople('successMessage.onDelete');
        await requestWithMessage({
            key: 'delete-user',
            startMessage,
            finishMessage,
            apiRequest: async () => {
                await deleteUser({
                    id: user?.id,
                    role: user?.role?.authority
                });
                const targetUrl = closeTabAndGetNewTargetUrl(`USER-${user?.id}`);
                redirectTo(targetUrl); // re-direct to filters page
            },
            errorConfig: {
                onApiRequestError: (error: any) => {
                    if (error?.response?.data?.message === 'This user has unsolved tickets, so you cannot delete')
                        grispiNotification('error', t('deleteError'), 'error');
                    else grispiNotification('error', error?.response?.data?.message || error?.message, 'error');
                },
                showAutoErrorMessage: false
            }
        });
    };
    const items: MenuProps['items'] = [
        { label: t('mergeUser'), key: UserActionMap.merge },
        { label: t('delete'), key: UserActionMap.delete }
    ]
        .filter(({ key }) => !(key === UserActionMap.merge && !isMergableUser))
        .filter(({ key }) => !(key === UserActionMap.delete && !currentUserCanDeleteCustomer));

    const menuProps = {
        items,
        onClick: handleMenuClick
    };

    return (
        <Row justify="center">
            {items.length > 0 && (
                <Dropdown menu={menuProps} placement="bottomRight" trigger={['click']}>
                    <Button icon={<DownOutlined style={{ fontSize: 12 }} />} />
                </Dropdown>
            )}

            <UserMergeModal user={user} ref={modalRef} />
            {contextHolder}
        </Row>
    );
}
